import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { Home, DollarSign, CalendarDays, CalendarCheck, Clock } from "lucide-react";

// Today's date as YYYY-MM-DD in the user's local timezone (not UTC, so it
// doesn't slip a day either side of midnight).
const localToday = () => {
    const d = new Date();
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().split("T")[0];
};

// Lease duration presets. "months" drives the auto-calculated tenancy end date;
// "Periodic" (month-to-month) leaves the end date open.
const DURATION_OPTIONS = [
    { value: "", label: "Select lease duration" },
    { value: "6", label: "6 months" },
    { value: "12", label: "12 months" },
    { value: "18", label: "18 months" },
    { value: "24", label: "24 months" },
    { value: "periodic", label: "Periodic (month-to-month)" },
];

// Add N months to a YYYY-MM-DD date, clamping to the end of the target month
// (e.g. 31 Jan + 1 month -> 28/29 Feb) and returning YYYY-MM-DD.
const addMonths = (isoDate, months) => {
    if (!isoDate) return "";
    const [y, m, d] = isoDate.split("-").map((n) => parseInt(n, 10));
    if (!y || !m || !d) return "";
    const base = new Date(y, m - 1, d);
    const targetMonth = base.getMonth() + months;
    const end = new Date(base.getFullYear(), targetMonth, base.getDate());
    // If the day rolled over (e.g. Feb has no 30th), step back to last valid day.
    if (end.getDate() !== base.getDate()) {
        end.setDate(0);
    }
    const tzOffset = end.getTimezoneOffset() * 60000;
    return new Date(end.getTime() - tzOffset).toISOString().split("T")[0];
};

/**
 * Modal shown when an agent clicks "Mark as Leased" on a rental listing —
 * the "Create New Tenancy" screen.
 *
 * Captures:
 *  - the lease amount (weekly rent)
 *  - an optional letting/leasing fee (inc GST)
 *  - the tenancy start date (defaults to today)
 *  - the tenancy end date (auto-filled when a fixed-term duration is picked)
 *  - the lease duration preset (helper that computes the end date)
 *
 * onConfirm receives:
 *   {
 *     leaseAmount: number,          // weekly rent
 *     lettingFee: number | null,    // fixed fee inc GST (null when blank)
 *     tenancyStart: string ("YYYY-MM-DD" — defaults to today),
 *     tenancyEnd: string ("YYYY-MM-DD" or ""),
 *     duration: string,             // preset value ("12", "periodic", ...)
 *   }
 */
const MarkLeasedModal = ({
    isOpen,
    onClose,
    onConfirm,
    loading = false,
    address = "",
    initialLeaseAmount = "",
    initialLettingFee = "",
    initialTenancyStart = "",
    initialTenancyEnd = "",
}) => {
    const [leaseAmount, setLeaseAmount] = useState("");
    const [lettingFee, setLettingFee] = useState("");
    const [tenancyStart, setTenancyStart] = useState("");
    const [tenancyEnd, setTenancyEnd] = useState("");
    const [duration, setDuration] = useState("");
    const [error, setError] = useState("");

    // Reset the form each time the modal is opened
    useEffect(() => {
        if (isOpen) {
            setLeaseAmount(
                initialLeaseAmount !== null && initialLeaseAmount !== undefined && initialLeaseAmount !== ""
                    ? String(initialLeaseAmount)
                    : "",
            );
            setLettingFee(
                initialLettingFee !== null && initialLettingFee !== undefined && initialLettingFee !== ""
                    ? String(initialLettingFee)
                    : "",
            );
            setTenancyStart(initialTenancyStart || localToday());
            setTenancyEnd(initialTenancyEnd || "");
            setDuration("");
            setError("");
        }
    }, [isOpen, initialLeaseAmount, initialLettingFee, initialTenancyStart, initialTenancyEnd]);

    // Keep only digits and a single decimal point as the user types
    const cleanMoney = (raw) => {
        const cleaned = raw.replace(/[^0-9.]/g, "");
        const parts = cleaned.split(".");
        return parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : cleaned;
    };

    // Group the integer part with thousands separators for display
    const groupThousands = (val) => {
        if (!val) return "";
        const [intPart, decPart] = val.split(".");
        const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return decPart !== undefined ? `${grouped}.${decPart}` : grouped;
    };

    const handleLeaseAmountChange = (e) => {
        setLeaseAmount(cleanMoney(e.target.value));
        if (error) setError("");
    };

    const handleLettingFeeChange = (e) => {
        setLettingFee(cleanMoney(e.target.value));
    };

    // Picking a fixed-term duration auto-fills the tenancy end from the start date.
    const handleDurationChange = (e) => {
        const value = e.target.value;
        setDuration(value);
        if (value && value !== "periodic") {
            setTenancyEnd(addMonths(tenancyStart || localToday(), parseInt(value, 10)));
        } else if (value === "periodic") {
            setTenancyEnd("");
        }
    };

    const handleStartChange = (e) => {
        const value = e.target.value;
        setTenancyStart(value);
        // Keep the end date in step when a fixed-term duration is selected.
        if (duration && duration !== "periodic") {
            setTenancyEnd(addMonths(value, parseInt(duration, 10)));
        }
        if (error) setError("");
    };

    const handleConfirm = () => {
        const amount = parseFloat(leaseAmount);
        if (!leaseAmount || isNaN(amount) || amount <= 0) {
            setError("Enter the weekly lease amount.");
            return;
        }
        const feeVal = lettingFee ? parseFloat(lettingFee) : null;
        onConfirm({
            leaseAmount: amount,
            lettingFee: feeVal !== null && !isNaN(feeVal) ? feeVal : null,
            tenancyStart: tenancyStart || localToday(),
            tenancyEnd: tenancyEnd || "",
            duration,
        });
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[460px] rounded-2xl border-none shadow-2xl max-h-[90vh] grid-rows-[auto_1fr_auto] overflow-hidden">
                <DialogHeader>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-teal-100 text-teal-600">
                            <Home className="w-6 h-6" />
                        </div>
                        <div>
                            <DialogTitle className="text-xl font-bold text-gray-900 leading-tight">
                                Create New Tenancy
                            </DialogTitle>
                            {address ? (
                                <p className="text-xs font-medium text-gray-400 mt-1 truncate max-w-[280px]">
                                    {address}
                                </p>
                            ) : null}
                        </div>
                    </div>
                </DialogHeader>

                <div className="py-2 space-y-4 overflow-y-auto min-h-0 -mr-2 pr-2">
                    {/* Lease amount (weekly rent) */}
                    <div>
                        <label
                            htmlFor="mark-leased-amount"
                            className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5"
                        >
                            Lease Amount <span className="text-red-500">*</span>
                        </label>
                        <div
                            className={`flex items-center rounded-xl border bg-white transition-all focus-within:ring-2 ${
                                error
                                    ? "border-red-300 focus-within:ring-red-200"
                                    : "border-gray-200 focus-within:ring-[#55C5D0]/40"
                            }`}
                        >
                            <span className="flex items-center justify-center w-10 h-11 text-gray-400">
                                <DollarSign className="w-4 h-4" />
                            </span>
                            <input
                                id="mark-leased-amount"
                                type="text"
                                inputMode="decimal"
                                autoFocus
                                value={groupThousands(leaseAmount)}
                                onChange={handleLeaseAmountChange}
                                placeholder="0.00"
                                disabled={loading}
                                className="flex-1 h-11 bg-transparent text-sm font-semibold text-gray-900 placeholder:text-gray-300 focus:outline-none"
                            />
                            <span className="flex items-center justify-center px-3 h-11 text-xs font-semibold text-gray-400 border-l border-gray-200">
                                / week
                            </span>
                        </div>
                    </div>

                    {/* Letting / leasing fee */}
                    <div>
                        <label
                            htmlFor="mark-leased-fee"
                            className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5"
                        >
                            Letting / Leasing Fee
                        </label>
                        <div className="flex items-center rounded-xl border border-gray-200 bg-white transition-all focus-within:ring-2 focus-within:ring-[#55C5D0]/40">
                            <span className="flex items-center justify-center w-10 h-11 text-gray-400">
                                <DollarSign className="w-4 h-4" />
                            </span>
                            <input
                                id="mark-leased-fee"
                                type="text"
                                inputMode="decimal"
                                value={groupThousands(lettingFee)}
                                onChange={handleLettingFeeChange}
                                placeholder="0.00"
                                disabled={loading}
                                className="flex-1 h-11 bg-transparent text-sm font-semibold text-gray-900 placeholder:text-gray-300 focus:outline-none"
                            />
                            <span className="flex items-center justify-center px-3 h-11 text-xs font-semibold text-gray-400 border-l border-gray-200">
                                inc GST
                            </span>
                        </div>
                    </div>

                    {/* Tenancy start */}
                    <div>
                        <label
                            htmlFor="mark-leased-start"
                            className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5"
                        >
                            Tenancy Start <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center rounded-xl border border-gray-200 bg-white transition-all focus-within:ring-2 focus-within:ring-[#55C5D0]/40">
                            <span className="flex items-center justify-center w-10 h-11 text-gray-400">
                                <CalendarCheck className="w-4 h-4" />
                            </span>
                            <input
                                id="mark-leased-start"
                                type="date"
                                value={tenancyStart}
                                onChange={handleStartChange}
                                disabled={loading}
                                className="flex-1 h-11 pr-3 bg-transparent text-sm font-semibold text-gray-900 focus:outline-none"
                            />
                        </div>
                    </div>

                    {/* Lease duration (helper — computes the end date) */}
                    <div>
                        <label
                            htmlFor="mark-leased-duration"
                            className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5"
                        >
                            Duration
                        </label>
                        <div className="flex items-center rounded-xl border border-gray-200 bg-white transition-all focus-within:ring-2 focus-within:ring-[#55C5D0]/40">
                            <span className="flex items-center justify-center w-10 h-11 text-gray-400">
                                <Clock className="w-4 h-4" />
                            </span>
                            <select
                                id="mark-leased-duration"
                                value={duration}
                                onChange={handleDurationChange}
                                disabled={loading}
                                className="flex-1 h-11 pr-3 bg-transparent text-sm font-semibold text-gray-900 focus:outline-none"
                            >
                                {DURATION_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Tenancy end */}
                    <div>
                        <label
                            htmlFor="mark-leased-end"
                            className="block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5"
                        >
                            Tenancy End
                        </label>
                        <div className="flex items-center rounded-xl border border-gray-200 bg-white transition-all focus-within:ring-2 focus-within:ring-[#55C5D0]/40">
                            <span className="flex items-center justify-center w-10 h-11 text-gray-400">
                                <CalendarDays className="w-4 h-4" />
                            </span>
                            <input
                                id="mark-leased-end"
                                type="date"
                                value={tenancyEnd}
                                onChange={(e) => setTenancyEnd(e.target.value)}
                                disabled={loading}
                                className="flex-1 h-11 pr-3 bg-transparent text-sm font-semibold text-gray-900 focus:outline-none"
                            />
                        </div>
                        <p className="text-[11px] font-medium text-gray-400 mt-1">
                            Optional. Auto-filled when you pick a fixed-term duration; leave blank for a periodic lease.
                        </p>
                    </div>

                    {error ? (
                        <p className="text-xs font-medium text-red-500">{error}</p>
                    ) : null}
                </div>

                <DialogFooter className="gap-3 sm:gap-0 sm:flex-row flex-col">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="flex-1 px-6 py-3 text-xs font-black uppercase tracking-widest text-gray-500 bg-gray-100 border border-transparent rounded-xl hover:bg-gray-200 transition-all font-sans"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={loading}
                        className="flex-1 px-6 py-3 text-xs font-black uppercase tracking-widest text-white rounded-xl transition-all font-sans shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 bg-[#55C5D0] hover:bg-[#4AADB7] focus:ring-[#55C5D0]"
                    >
                        {loading ? (
                            <>
                                <span className="w-3 h-3 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                Processing...
                            </>
                        ) : (
                            "Set As Leased"
                        )}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default MarkLeasedModal;
